/**
 * Signal pub/sub module-level pour déclencher un rechargement IN-PLACE de la galerie
 * DEPUIS L'EXTÉRIEUR — c.-à-d. sans que la galerie soit à l'origine de l'action.
 *
 * Cas d'usage : un scan qui a réellement indexé/retiré des fichiers se termine alors
 * qu'il N'A PAS été lancé depuis la galerie (scan de fond, autre appareil/session).
 * La cloche de notifications (montée dans la coquille) détecte ce cas et appelle
 * `requestGalleryRefresh()` ; `GalleryView` s'abonne via `subscribeGalleryRefresh` et
 * recharge sa fenêtre de données sans navigation ni remontage.
 *
 * Découplage volontaire SANS portail ni contexte React : un émetteur (la cloche) et un
 * récepteur (la galerie) qui ne se connaissent pas et peuvent être montés/démontés
 * indépendamment. Même esprit que les stores module-level du projet (`job-activity`,
 * `CountsSource`). Aucune dépendance à React ici — juste un `Set` de listeners.
 *
 * ---------------------------------------------------------------------------
 * SYNCHRONISATION ENTRE ONGLETS (audit de remédiation, constat A7)
 * ---------------------------------------------------------------------------
 * CONSTAT : le signal était strictement INTRA-ONGLET, et son unique émetteur était la
 * cloche de fin de job. Mettre une photo à la corbeille dans l'onglet A laissait donc
 * l'onglet B périmé — un clic sur la vignette disparue rendait un 404. Seules les
 * mutations passant par un JOB étaient propagées, et seulement dans l'onglet qui les
 * observait.
 *
 * CORRECTIF : le signal est diffusé sur un `BroadcastChannel('xphoto-gallery')`. Tout
 * `requestGalleryRefresh()` notifie les abonnés LOCAUX **et** poste un message ; les
 * autres onglets de la même origine le reçoivent et notifient leurs abonnés locaux.
 *
 * Deux garde-fous :
 *  · AUCUNE BOUCLE — la réception appelle `notifyLocal`, jamais `requestGalleryRefresh` :
 *    un message reçu n'est jamais réémis (deux onglets ne peuvent pas se renvoyer la
 *    balle indéfiniment). `BroadcastChannel` ne livre d'ailleurs pas à l'émetteur.
 *  · GARDE DÉFENSIVE — `BroadcastChannel` n'existe pas partout (happy-dom des tests
 *    web, navigateurs anciens, contextes restreints). Son absence, ou un `postMessage`
 *    qui lève (canal fermé), dégrade PROPREMENT vers le comportement d'origine
 *    (intra-onglet) : jamais une exception remontée à l'appelant.
 *
 * Les APPELANTS SONT INCHANGÉS : `requestGalleryRefresh` / `subscribeGalleryRefresh`
 * gardent exactement leur signature et leur sémantique locale.
 */

/** Listeners abonnés au signal de rafraîchissement (identité de fonction = clé). */
const listeners = new Set<() => void>();

/** Nom du canal de diffusion inter-onglets (constante d'origine, jamais de l'entrée). */
const CHANNEL_NAME = 'xphoto-gallery';

/**
 * Canal de diffusion, ou `null` si l'environnement ne le fournit pas (happy-dom, vieux
 * navigateur). Créé UNE fois au chargement du module, comme le `Set` de listeners.
 */
const channel: BroadcastChannel | null = createChannel();

function createChannel(): BroadcastChannel | null {
  try {
    if (typeof BroadcastChannel === 'undefined') return null;
    const bc = new BroadcastChannel(CHANNEL_NAME);
    // Un message reçu = un autre onglet a muté des données → on notifie SEULEMENT nos
    // abonnés locaux (pas de réémission : cf. « aucune boucle » ci-dessus).
    bc.onmessage = () => {
      notifyLocal();
    };
    return bc;
  } catch {
    // Contexte restreint : on dégrade vers le mode intra-onglet.
    return null;
  }
}

/**
 * Abonne un `listener` au signal de rafraîchissement de la galerie.
 * Renvoie une fonction de désinscription (à appeler au démontage).
 */
export function subscribeGalleryRefresh(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Notifie les abonnés de CET onglet. Chaque appel est isolé dans un `try/catch` — un
 * listener qui lève N'EMPÊCHE PAS les autres d'être notifiés (exception avalée
 * silencieusement, aucun état partagé à corrompre).
 */
function notifyLocal(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Un listener défaillant ne doit pas interrompre la diffusion aux autres.
    }
  }
}

/**
 * Publie le signal : notifie les abonnés locaux, PUIS diffuse aux autres onglets de la
 * même origine (best-effort — une diffusion impossible n'affecte pas l'onglet courant).
 */
export function requestGalleryRefresh(): void {
  notifyLocal();
  broadcastGalleryRefresh();
}

/**
 * Diffuse le signal aux AUTRES onglets UNIQUEMENT, sans notifier les abonnés locaux.
 *
 * Réservé au composant qui vient LUI-MÊME d'appliquer la mutation et qui recharge déjà
 * son propre état (la galerie après une mise à la corbeille) : passer par
 * `requestGalleryRefresh` y provoquerait un second rechargement redondant du même onglet.
 * Best-effort — sans `BroadcastChannel`, c'est un no-op silencieux.
 */
export function broadcastGalleryRefresh(): void {
  try {
    channel?.postMessage('refresh');
  } catch {
    // Canal fermé / indisponible : dégradation propre vers le mode intra-onglet.
  }
}
